import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import { DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT } from '@ipfabric/ui-components/hooks/useDebounce';
import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { FormikFilterTextField } from 'components/FormikFilterTextField/FormikFilterTextField';
import { getFilterValue } from 'components/FormikFilterTextField/comparator';
import {
  getNumberFilterValueValidationErrors,
  getStringFilterValueValidationErrors,
} from 'components/FormikFilterTextField/validations';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import {
  initialTechnologies,
  isTopologyGraphMetadata,
} from 'components/Graph/GraphState';
import { updateStpInstanceIsolate } from 'components/Graph/commands/updateStpInstanceIsolate';
import { selectDeviceSns } from 'components/Graph/model/selectors/selectDeviceSns';
import { IsolateInstancesToggle } from 'components/IsolateInstancesToggle/IsolateInstancesToggle';
import { ScrollPaginator } from 'components/ScrollPaginator/ScrollPaginator';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, useFormik } from 'formik';
import type React from 'react';
import { useState } from 'react';
import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import type { AppTranslations } from 'translations/translations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useAutoSubmit } from 'utils/hooks/useAutoSubmit';
import { SearchIcon } from '../icons/SearchIcon';
import { GraphStpInstance } from './GraphStpInstance';
import type { StpInstancesHook } from './useStpInstances';

interface FilterFormValues {
  vlanName: string;
  vlanId: string;
  rootHostname: string;
  rootId: string;
}

const makeValidate =
  (msg: AppTranslations) =>
  (values: FilterFormValues): FormikErrors<FilterFormValues> => {
    const errors: FormikErrors<FilterFormValues> = {};

    const vlanIdErrors = getNumberFilterValueValidationErrors(
      msg,
      values.vlanId,
    );
    const rootIdErrors = getStringFilterValueValidationErrors(
      msg,
      values.rootId,
    );
    const rootHostnameErrors = getStringFilterValueValidationErrors(
      msg,
      values.rootHostname,
    );
    const vlanNameErrors = getStringFilterValueValidationErrors(
      msg,
      values.vlanName,
    );

    if (vlanIdErrors) {
      errors.vlanId = vlanIdErrors;
    }

    if (rootIdErrors) {
      errors.rootId = rootIdErrors;
    }

    if (rootHostnameErrors) {
      errors.rootHostname = rootHostnameErrors;
    }

    if (vlanNameErrors) {
      errors.vlanName = vlanNameErrors;
    }

    return errors;
  };

const getWarningOrErrorMessage = (
  msg: AppTranslations,
  loadingState: LoadingState,
  hasData: boolean,
) => {
  let message: React.ReactNode;
  if (loadingState === 'ERROR') {
    message = (
      <span className='text-danger'>
        {msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances.error}
      </span>
    );
  } else if (!hasData) {
    message = msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances.noData;
  } else {
    message =
      msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances.notFound;
  }

  return <p className='mt-lgu-6'>{message}</p>;
};

interface Props {
  stpInstancesHook: StpInstancesHook;
}

export const GraphStpInstances: FC<Props> = (props) => {
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const msg = useLocale();
  const isolateId = useHtmlId('isolate');

  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    appliedStpInstances: (s) =>
      isTopologyGraphMetadata(s.graphMetadata)
        ? s.graphMetadata.parameters.technologies?.stpInstances
        : undefined,
    selectedInstancesCount: (s) =>
      s.technologies.selectedTechnologies.stpInstances?.instances.filter(
        ({ visible }) => visible,
      ).length,
    snapshotId: prop('snapshotId'),
    sns: selectDeviceSns,
    stpInstances: (s) =>
      s.technologies.selectedTechnologies.stpInstances ??
      initialTechnologies.stpInstances,
  });

  const toggleFilter = () => {
    setFilterVisible(!filterVisible);
  };

  const {
    clearNotPresentInstances,
    handleLoadMoreInstances,
    hasData,
    hasSelectedInstances,
    instanceData,
    loadInstanceFirstPage,
    loadingState,
    notPresentSelectedInstances,
    visibleAppliedInstances,
  } = props.stpInstancesHook;

  const handleSubmit = (values: FilterFormValues) => {
    const filters = {
      ...(values.vlanName
        ? { vlanName: getFilterValue(values.vlanName.trim(), 'string') }
        : {}),
      ...(values.vlanId
        ? { vlanId: getFilterValue(values.vlanId.trim(), 'number') }
        : {}),
      ...(values.rootId
        ? { rootId: getFilterValue(values.rootId.trim(), 'string') }
        : {}),
      ...(values.rootHostname
        ? { rootHostname: getFilterValue(values.rootHostname.trim(), 'string') }
        : {}),
    };

    loadInstanceFirstPage(filters);
  };

  const toggleOpen = () => {
    setOpen(!open);
  };

  const handleToggleIsolate = (checked: boolean) => {
    ctxProps.dispatch(updateStpInstanceIsolate, { isolate: checked });
  };

  const formik = useFormik({
    initialValues: {
      vlanName: '',
      vlanId: '',
      rootHostname: '',
      rootId: '',
    },
    validate: makeValidate(msg),
    onSubmit: makeFormikOnSubmit(handleSubmit),
  });

  useAutoSubmit(formik, DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT);

  return (
    <div
      className={classNames(
        'graph-stp-instances',
        open && 'graph-stp-instances--open py-lgu-5 pr-5',
      )}
    >
      <button
        className='graph-stp-instances__toggle-button'
        type='button'
        onClick={toggleOpen}
      >
        {ctxProps.selectedInstancesCount ? (
          <FormattedMessage
            id='canvasGraph.graphSidePanel.layoutSettings.selectedInstances'
            defaultMessage={
              msg.canvasGraph.graphSidePanel.layoutSettings.selectedInstances
            }
            values={{
              selected: ctxProps.selectedInstancesCount,
              count: visibleAppliedInstances.length + (instanceData.count ?? 0),
            }}
          />
        ) : (
          <FormattedMessage
            id='canvasGraph.graphSidePanel.layoutSettings.stpInstances.instancesCount'
            defaultMessage={
              msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                .instancesCount
            }
            values={{
              count: visibleAppliedInstances.length + (instanceData.count ?? 0),
            }}
          />
        )}
        <i
          className={classNames(
            'fa graph-stp-instances__toggle-button-icon',
            loadingState !== 'LOADING' &&
              (open ? 'fa-chevron-up' : 'fa-chevron-down'),
            loadingState === 'LOADING' && 'fa-spinner fa-spin',
          )}
        />
      </button>
      {open && (
        <>
          <div className='mb-2.5 flex justify-between font-bold'>
            <div
              className='flex cursor-pointer select-none items-center'
              onClick={toggleFilter}
            >
              <SearchIcon title={null} height={16} width={16} />
              <ButtonAsA className='pl-lgu-3'>
                {
                  msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                    .findInstances
                }
              </ButtonAsA>
            </div>
            <IsolateInstancesToggle
              checked={!!ctxProps.stpInstances.isolate}
              disabled={!hasSelectedInstances}
              id={isolateId}
              labelText={
                msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                  .isolateInstances
              }
              labelTooltipText={
                msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                  .isolateInstancesTooltip
              }
              onChange={handleToggleIsolate}
              wrapperClassName='pr-lgu-3'
            />
          </div>
          {filterVisible && (
            <form className='my-lgu-6'>
              <div className='flex'>
                <div className='graph-stp-instances__vlan-filter pr-lgu-1'>
                  <FormikFilterTextField
                    autoTrim='left'
                    className='ipf-input w-full'
                    formik={formik}
                    name='vlanName'
                    type='string'
                    placeholder={
                      msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                        .filters.vlanName
                    }
                  />
                </div>
                <div className='pl-lgu-1 flex-1'>
                  <FormikFilterTextField
                    autoTrim='left'
                    className='ipf-input w-full'
                    formik={formik}
                    name='rootHostname'
                    type='string'
                    placeholder={
                      msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                        .filters.rootHostname
                    }
                  />
                </div>
              </div>
              <div className='mt-2.5 flex'>
                <div className='graph-stp-instances__vlan-filter pr-lgu-1'>
                  <FormikFilterTextField
                    autoTrim='all'
                    className='ipf-input w-full'
                    formik={formik}
                    name='vlanId'
                    type='number'
                    placeholder={
                      msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                        .filters.vlanId
                    }
                  />
                </div>
                <div className='pl-lgu-1 flex-1'>
                  <FormikFilterTextField
                    autoTrim='left'
                    className='ipf-input w-full'
                    formik={formik}
                    name='rootId'
                    type='string'
                    placeholder={
                      msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                        .filters.rootId
                    }
                  />
                </div>
              </div>
            </form>
          )}
          {notPresentSelectedInstances.length > 0 && (
            <p>
              <FormattedMessage
                id='canvasGraph.graphSidePanel.layoutSettings.stpInstances.notPresetnSelected'
                defaultMessage={
                  msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                    .notPresetnSelected
                }
                values={{ count: notPresentSelectedInstances.length }}
              />{' '}
              <ButtonAsA onClick={clearNotPresentInstances}>
                {
                  msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                    .remove
                }
              </ButtonAsA>
            </p>
          )}
          {instanceData.instances.length === 0 ||
          loadingState === 'ERROR' ||
          !hasData ? (
            getWarningOrErrorMessage(msg, loadingState, hasData)
          ) : (
            <>
              <div className='graph-stp-instances__labels flex'>
                <div className='flex-1 font-bold'>
                  {
                    msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                      .vlanLabel
                  }
                </div>
                <div className='flex-1 font-bold'>
                  {
                    msg.canvasGraph.graphSidePanel.layoutSettings.stpInstances
                      .rootLabel
                  }
                </div>
              </div>
              <div className='graph-stp-instances__list'>
                <ScrollPaginator
                  loaded={instanceData.instances.length}
                  total={instanceData.count}
                  onFetch={handleLoadMoreInstances}
                >
                  {instanceData.instances.map((instance) => (
                    <GraphStpInstance
                      key={instance.id}
                      instanceData={instance}
                      instances={ctxProps.stpInstances.instances}
                    />
                  ))}
                </ScrollPaginator>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
